import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { QueueLayout } from "../../features/web3/forex/components/QueueLayout";
import { Td } from "../../features/web3/forex/components/table";
import { KIND, PURPOSE, TIER } from "./api";
import { AccountLink, ListView, Money, Panel, UserLink, mono, useTab } from "./parts";
import { orderColumns, orderRow, positionColumns, positionRow } from "./rows";

const TABS = [
  { value: "positions", label: "Open positions" },
  { value: "orders", label: "Pending orders" },
  { value: "exposure", label: "Exposure" },
  { value: "margin-risk", label: "Margin risk" },
];

const OWNER_FIELDS = [
  { name: "login", label: "MT5 login" },
  { name: "username", label: "Username (exact)" },
  { name: "symbol", label: "Symbol (exact, e.g. XAUUSD)" },
  { name: "kind", label: "Kind", options: KIND },
];
const OWNER_EMPTY = { login: "", username: "", symbol: "", kind: "" };

const ORDER_TYPES = [
  "BUY", "SELL", "BUY_LIMIT", "SELL_LIMIT", "BUY_STOP", "SELL_STOP",
  "BUY_STOP_LIMIT", "SELL_STOP_LIMIT", "CLOSE_BY",
].map((v) => ({ value: v, label: v.replaceAll("_", " ") }));

const EXPOSURE_COLUMNS = [
  { label: "Symbol" },
  { label: "Buy lots", align: "right" },
  { label: "Sell lots", align: "right" },
  { label: "Net lots", align: "right" },
  { label: "Buys", align: "right" },
  { label: "Sells", align: "right" },
  { label: "Floating", align: "right" },
];

const exposureRow = (r) => (
  <tr key={r.symbol} className="hover:bg-white/[0.02]">
    <Td className={mono}>{r.symbol}</Td>
    <Td align="right" className={mono}>{r.buyLots}</Td>
    <Td align="right" className={mono}>{r.sellLots}</Td>
    {/* Negative = the book leans short on this symbol. */}
    <Td align="right"><Money value={r.netLots} /></Td>
    <Td align="right" className={mono}>{r.buyCount}</Td>
    <Td align="right" className={mono}>{r.sellCount}</Td>
    <Td align="right"><Money value={r.floatingProfit} /></Td>
  </tr>
);

const RISK_TONE = {
  NEAR_MARGIN_CALL: "text-amber-400",
  MARGIN_CALL: "text-orange-400",
  STOP_OUT: "text-red-400",
};

const RISK_COLUMNS = [
  { label: "Status" },
  { label: "Login" },
  { label: "Username" },
  { label: "Kind" },
  { label: "Tier" },
  { label: "Purpose" },
  { label: "Margin level", align: "right" },
  { label: "Margin call at", align: "right" },
  { label: "Stop out at", align: "right" },
  { label: "Equity", align: "right" },
  { label: "Margin", align: "right" },
  { label: "Free margin", align: "right" },
  { label: "Floating", align: "right" },
];

const riskRow = (r) => (
  <tr key={r.id} className="hover:bg-white/[0.02]">
    <Td className={`whitespace-nowrap text-xs font-medium ${RISK_TONE[r.status]}`}>{r.status.replaceAll("_", " ")}</Td>
    <Td className={mono}><AccountLink id={r.id}>{r.login}</AccountLink></Td>
    <Td className={mono}><UserLink username={r.username} /></Td>
    <Td className="text-xs">{r.kind}</Td>
    <Td className="text-xs">{r.tier}</Td>
    <Td className="text-xs">{r.purpose.replaceAll("_", " ")}</Td>
    <Td align="right" className={mono}>{Number(r.marginLevel).toFixed(2)}%</Td>
    {/* Each group has its own levels, so they are shown per row. */}
    <Td align="right" className={mono}>{r.marginCall}%</Td>
    <Td align="right" className={mono}>{r.stopOut}%</Td>
    <Td align="right"><Money value={r.equity} /></Td>
    <Td align="right"><Money value={r.margin} /></Td>
    <Td align="right"><Money value={r.freeMargin} /></Td>
    <Td align="right"><Money value={r.floatingProfit} /></Td>
  </tr>
);


function Tab({ tab }) {
  switch (tab) {
    case "orders":
      return (
        <ListView path="/accounts/admin/orders" empty={{ ...OWNER_EMPTY, type: "" }}
          fields={[...OWNER_FIELDS, { name: "type", label: "Order type", options: [{ value: "", label: "All types" }, ...ORDER_TYPES] }]}
          columns={orderColumns(true)} row={orderRow(true)} label="orders" />
      );
    case "exposure":
      return (
        <ListView
          path="/accounts/admin/exposure"
          paged={false}
          empty={{ kind: "REAL", tier: "", purpose: "" }}
          fields={[
            { name: "kind", label: "Kind", options: KIND.slice(1) },
            { name: "tier", label: "Tier", options: TIER },
            { name: "purpose", label: "Purpose", options: PURPOSE },
          ]}
          columns={EXPOSURE_COLUMNS}
          row={exposureRow}
          rowsOf={(d) => d}
        />
      );
    case "margin-risk":
      return (
        <div className="space-y-8">
          <Panel title="Margin risk">
            <ListView
              path="/accounts/admin/margin-risk"
              empty={{ below: "100", status: "", kind: "" }}
              fields={[
                { name: "below", label: "Margin level under (%)", type: "number" },
                { name: "status", label: "Status", options: [
                  { value: "", label: "All" },
                  { value: "NEAR_MARGIN_CALL", label: "Near margin call" },
                  { value: "MARGIN_CALL", label: "Margin call" },
                  { value: "STOP_OUT", label: "Stop out" },
                ] },
                { name: "kind", label: "Kind", options: KIND },
              ]}
              columns={RISK_COLUMNS}
              row={riskRow}
              label="accounts"
            />
          </Panel>
        </div>
      );
    default:
      return (
        <ListView path="/accounts/admin/positions" empty={OWNER_EMPTY} fields={OWNER_FIELDS}
          columns={positionColumns(true)} row={positionRow(true)} label="positions" />
      );
  }
}

export default function LiveBook() {
  const [tab, setTab] = useTab("positions");
  const qc = useQueryClient();
  const fetching = useIsFetching({ queryKey: ["forex-admin"] }) > 0;

  return (
    <QueueLayout
      eyebrow="Forex Admin"
      title="Live"
      accent="book"
      description="What our accounts hold right now: open positions, pending orders, net lots per symbol, and accounts close to a stop out."
      statusOptions={TABS}
      status={tab}
      onStatus={setTab}
      isFetching={fetching}
      onRefresh={() => qc.invalidateQueries({ queryKey: ["forex-admin"] })}
    >
      <Tab key={tab} tab={tab} />
    </QueueLayout>
  );
}
